/**
 * screens/modals/useModalSubmit.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The submit flow every modal shares, in one place.
 *
 * Thirteen dialogs all do the same five things: validate, call a service,
 * confirm with the backend's own message, invalidate the data behind the
 * screen, and close. Writing that out thirteen times is thirteen chances to
 * forget the invalidate — and a modal that saves without refreshing the table
 * behind it looks like it did nothing.
 *
 * `validate` returns an error STRING (shown as the design's instruction-style
 * validation toast) or null when the form is complete. It runs before any
 * request goes out, so an incomplete form costs no round trip.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useState } from 'react';
import { handleApiError } from '../../utils/errorHandler';
import { showCompleted, showSuccess, showValidation } from '../../utils/successHandler';
import { useShell } from '../../context/ShellContext';

export interface ModalSubmitOptions<T> {
  /** Returns the instruction to show, or null when the form is complete. */
  validate?: () => string | null;
  /** The write. Throws on failure; the error handler reports it. */
  submit: () => Promise<T>;
  /** Toast title on success — the action in the product's own vocabulary. */
  title: string;
  /** Shown when the API sends no message of its own. */
  fallback: string;
  /** Green rail for a completion; plain for a neutral state change. */
  tone?: 'completed' | 'plain';
  /** Runs after a successful write, before the modal closes. */
  onDone?: (result: T) => void;
  /** Keep the modal open — used where the result is worth reading in place. */
  keepOpen?: boolean;
}

export interface ModalSubmitState {
  submitting: boolean;
  run: () => Promise<void>;
}

export function useModalSubmit<T>({
  validate,
  submit,
  title,
  fallback,
  tone = 'completed',
  onDone,
  keepOpen = false,
}: ModalSubmitOptions<T>): ModalSubmitState {
  const { closeModal, invalidate } = useShell();
  const [submitting, setSubmitting] = useState(false);

  const run = useCallback(async () => {
    const problem = validate?.() ?? null;
    if (problem) {
      showValidation(problem);
      return;
    }

    setSubmitting(true);
    try {
      const result = await submit();
      if (tone === 'completed') showCompleted(result, title, fallback);
      else showSuccess(result, title, fallback);
      onDone?.(result);
      // Always invalidate: a write here changes data on the screen behind, and
      // often on two or three others.
      invalidate();
      if (!keepOpen) closeModal();
    } catch (error) {
      handleApiError(error, `${title} failed`);
    } finally {
      setSubmitting(false);
    }
  }, [validate, submit, title, fallback, tone, onDone, keepOpen, invalidate, closeModal]);

  return { submitting, run };
}
